/* eslint-disable import/no-internal-modules */
import Icon from '@insided/deprecated-preact-components/assets'
import SimplePanel from '@insided/deprecated-control/ui-components/SimplePanel'
import TextField from '@insided/deprecated-preact-components/design-system-components/text-field'
import InputUnit from '@insided/deprecated-preact-components/design-system-components/input-unit'
import VisibilitySettings from './VisibilitySettings'
import { Permission, Role, RoleList } from './interfaces'
import globals from '@insided/deprecated-utils/globals'
import { z } from 'zod'

interface GeneralPanelProps extends FieldsErrorType {
  title: string
  slug: string
  isSlugReadOnly?: boolean
  roles: RoleList
  selectedFilter?: string
  onTitleChange: (value: string) => void
  onSlugChange: (value: string) => void
  onVisibilitySettingsChange: (selectedRoles: Role[]) => void
  existingRules?: Permission[]
}

export interface FieldsErrorType {
  fieldsError: {
    title: string | null
    slug?: string | null
  }
}

export const INVALID_SLUG_GENERIC_MESSAGE = 'Invalid input'
export const INVALID_TITLE_GENERIC_MESSAGE = 'Invalid title'

export const ErrorSchemaForSlug = z
  .string()
  .nonempty('This field is required')
  .regex(/^(?!-).*[^-]$/, 'Slug can\'t start or end with "-"')
  .regex(/^((?!\/).)*$/, 'Slug cannot include "/"')
  .regex(/^((?!\s).)*$/, 'Slug cannot include any space')
  .regex(
    /^((?![:?#@\][!$&'()*+,;=]).)*$/,
    'Please do not use any of the following characters in slugs: ":", "?", "#", "[", "]", "@", "!", "$", "&", "\'", "(", ")", "*", "+", ",", ";", "="'
  )

export const ErrorSchemaForTitle = z.string().nonempty('This field is required')

export const checkInputValidation = (
  errorSchema: typeof ErrorSchemaForSlug | typeof ErrorSchemaForTitle,
  text: string,
  fallbackErrorMessage: string
): string | null => {
  try {
    const result = errorSchema.safeParse(text)
    if (!result.success) {
      const formatted = result.error.format()
      return formatted._errors[0]
    }
    return null // to achieve type checking
  } catch (e) {
    return fallbackErrorMessage
  }
}

const GeneralPanel = ({
  fieldsError,
  title,
  slug,
  isSlugReadOnly = false,
  onTitleChange,
  onSlugChange,
  onVisibilitySettingsChange,
  existingRules,
  roles
}: GeneralPanelProps) => (
  <SimplePanel
    className='custom-pages__general-panel'
    image={<Icon icon='edit' width={30} height={30} color='#2B3346' />}
  >
    <div className='custom-page__general-title'>
      <h5>General</h5>
    </div>
    <InputUnit error={fieldsError.title ?? undefined}>
      <TextField
        defaultValue={title}
        isInline={false}
        size='fullWidth'
        name='custom_page_name'
        label='Page Name'
        ariaLabel='Page Name'
        id='custom_page_name'
        onChange={(e) => onTitleChange(e)}
      />
    </InputUnit>
    <InputUnit error={fieldsError.slug ?? undefined}>
      <TextField
        defaultValue={slug}
        disabled={isSlugReadOnly}
        isInline={false}
        size='fullWidth'
        name='custom_page_slug'
        label='URL slug'
        subtext='URL slug cannot be edited once set'
        ariaLabel='URL slug'
        id='custom_page_url_slug'
        onChange={(e) => onSlugChange(e)}
        contextText={`${globals('communityUrl')}/p/`}
        tooltipMessage='URL slugs should be kept short, keyword focussed and include lower case letters, numbers and hyphens only.'
        tooltipHeading='✨ Best Practices for URL slug'
      />
    </InputUnit>
    <VisibilitySettings
      roles={roles}
      onVisibilitySettingsChange={onVisibilitySettingsChange}
      existingRules={existingRules}
    />
  </SimplePanel>
)

export default GeneralPanel
